/**
 * Experience Validation Utilities
 *
 * Validates player-created experiences with three severity levels:
 * - Errors: Must fix to proceed (empty, too short, too long)
 * - Warnings: Should reconsider (overly broad, supernatural abilities)
 * - Success: Valid experience
 *
 * Also checks for duplicate experiences.
 */

export interface ExperienceValidation {
  isValid: boolean;
  severity: 'error' | 'warning' | 'success';
  message: string;
}

// Minimum and maximum character lengths
export const MIN_EXPERIENCE_LENGTH = 3;
export const MAX_EXPERIENCE_LENGTH = 40;

/**
 * Overly broad terms that lack specificity
 * These generate warnings to encourage more descriptive experiences
 */
const BROAD_TERMS = [
  'lucky',
  'skilled',
  'talented',
  'gifted',
  'smart',
  'strong',
  'fast',
  'powerful',
  'experienced',
  'capable',
  'good',
  'great',
  'excellent',
  'superior',
  'best',
];

/**
 * Supernatural or mechanical abilities that should be class features
 * These generate warnings as they're too powerful for general experiences
 */
const SUPERNATURAL_ABILITIES = [
  'flight',
  'fly',
  'flying',
  'invulnerable',
  'invincible',
  'teleport',
  'invisible',
  'immortal',
  'supersonic',
  'superhuman',
  'magic spell',
  'cast spells',
  'fireball',
  'lightning',
];

/**
 * Validate a single experience name
 */
export const validateExperience = (name: string): ExperienceValidation => {
  // Error: Empty name
  if (!name || name.trim().length === 0) {
    return {
      isValid: false,
      severity: 'error',
      message: 'Experience cannot be empty',
    };
  }

  const trimmedName = name.trim();

  // Error: Too short
  if (trimmedName.length < MIN_EXPERIENCE_LENGTH) {
    return {
      isValid: false,
      severity: 'error',
      message: `Experience must be at least ${MIN_EXPERIENCE_LENGTH} characters`,
    };
  }

  // Error: Too long
  if (trimmedName.length > MAX_EXPERIENCE_LENGTH) {
    return {
      isValid: false,
      severity: 'error',
      message: `Experience must be ${MAX_EXPERIENCE_LENGTH} characters or less`,
    };
  }

  const lowerName = trimmedName.toLowerCase();

  // Warning: Overly broad term
  const foundBroadTerm = BROAD_TERMS.find(term => {
    // Match whole word or as part of compound (e.g., "Very Lucky" or "Lucky Person")
    const regex = new RegExp(`\\b${term}\\b`, 'i');
    return regex.test(lowerName);
  });

  if (foundBroadTerm) {
    return {
      isValid: true,
      severity: 'warning',
      message: `"${foundBroadTerm}" is quite broad - consider being more specific (e.g., "Lucky at Cards" instead of "Lucky")`,
    };
  }

  // Warning: Supernatural ability
  const foundSupernatural = SUPERNATURAL_ABILITIES.find(ability => {
    const regex = new RegExp(`\\b${ability}\\b`, 'i');
    return regex.test(lowerName);
  });

  if (foundSupernatural) {
    return {
      isValid: true,
      severity: 'warning',
      message: `"${foundSupernatural}" sounds like a supernatural ability - experiences should represent training, background, or personality traits`,
    };
  }

  // Success: Valid experience
  return {
    isValid: true,
    severity: 'success',
    message: 'Valid experience',
  };
};

/**
 * Check if two experiences are duplicates (case-insensitive)
 */
export const checkDuplicates = (
  experience1: string,
  experience2: string
): ExperienceValidation => {
  const name1 = experience1.trim().toLowerCase();
  const name2 = experience2.trim().toLowerCase();

  // Both empty - not a duplicate issue
  if (!name1 && !name2) {
    return {
      isValid: true,
      severity: 'success',
      message: 'No duplicates',
    };
  }

  // One empty - not a duplicate
  if (!name1 || !name2) {
    return {
      isValid: true,
      severity: 'success',
      message: 'No duplicates',
    };
  }

  // Both filled - check for duplicate
  if (name1 === name2) {
    return {
      isValid: false,
      severity: 'error',
      message: 'Both experiences cannot be the same',
    };
  }

  return {
    isValid: true,
    severity: 'success',
    message: 'No duplicates',
  };
};

/**
 * Validate both experiences together
 * Returns array of validations: [exp1Validation, exp2Validation, duplicateCheck]
 */
export const validateBothExperiences = (
  experience1: string,
  experience2: string
): [ExperienceValidation, ExperienceValidation, ExperienceValidation] => {
  const exp1Validation = validateExperience(experience1);
  const exp2Validation = validateExperience(experience2);
  const duplicateCheck = checkDuplicates(experience1, experience2);

  return [exp1Validation, exp2Validation, duplicateCheck];
};

/**
 * Check if user can proceed (no errors, warnings are allowed)
 */
export const canProceedWithExperiences = (
  experience1: string,
  experience2: string
): boolean => {
  const [exp1, exp2, duplicate] = validateBothExperiences(experience1, experience2);

  // All must be valid (severity !== 'error')
  // Warnings are allowed
  return (
    exp1.severity !== 'error' &&
    exp2.severity !== 'error' &&
    duplicate.severity !== 'error'
  );
};

/**
 * Get character counter color based on length
 */
export const getCharCounterColor = (length: number): 'normal' | 'warning' | 'error' => {
  if (length > MAX_EXPERIENCE_LENGTH) {
    return 'error';
  }
  if (length >= MAX_EXPERIENCE_LENGTH - 5) {
    return 'warning';
  }
  return 'normal';
};
